﻿import React, { forwardRef, ChangeEvent } from 'react';
import { styled, alpha } from '@mui/material/styles';
import InputBase from '@mui/material/InputBase';
import SearchIcon from '@mui/icons-material/Search';
import { TranslatedLabels } from '../../../../../../shared/types';
import { Translate } from '../../../../../../shared/LanguageService';

const Search = styled('div')(({ theme }) => ({
    position: 'relative',
    borderRadius: theme.shape.borderRadius,
    backgroundColor: alpha(theme.palette.common.white, 0.15),
    '&:hover': {
        backgroundColor: alpha(theme.palette.common.white, 0.25)
    },
    marginRight: 0,
    marginLeft: 0,
    width: '100%'
}));

const SearchIconWrapper = styled('div')(({ theme }) => ({
    padding: theme.spacing(0, 2),
    height: '100%',
    position: 'absolute',
    pointerEvents: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
    color: 'inherit',
    '& .MuiInputBase-input': {
        padding: theme.spacing(1, 1, 1, 0),
        // vertical padding + font size from searchIcon
        paddingLeft: `calc(1em + ${theme.spacing(4)})`,
        transition: theme.transitions.create('width'),
        fontSize: '16px',
        width: '200px',
        [theme.breakpoints.up('md')]: {
            width: '20ch'
        }
    }
}));

type ExternalProps = {
    value: string;
    onBlur: () => void;
    onChange: (event: ChangeEvent<HTMLInputElement>) => void;
    inputProps: any;
};

type SearchInputBaseProps = {
    labels: TranslatedLabels;
} & ExternalProps;

let SearchInputBase = forwardRef<HTMLInputElement, SearchInputBaseProps>((props, ref) => {
    const { value, onBlur, onChange, inputProps, labels } = props;

    function handleChange(event: ChangeEvent<HTMLInputElement>) {
        onChange(event);
    }

    function handleBlur() {
        if (inputProps.onBlur) {
            inputProps.onBlur();
        }
        if (onBlur) {
            onBlur();
        }
    }

    return (
        <Search ref={ref} data-testid="supersearch-input">
            <SearchIconWrapper>
                <SearchIcon style={{ width: '18px', height: '18px' }} />
            </SearchIconWrapper>
            <StyledInputBase
                placeholder={labels.search}
                inputProps={{
                    'aria-label': labels.search,
                    ...inputProps,
                    value,
                    onChange: handleChange,
                    onBlur: handleBlur
                }}
            />
        </Search>
    );
});

let TranslatedSearchInputBase = forwardRef<HTMLInputElement, ExternalProps>((props, ref) => {
    let labels: TranslatedLabels = {
        search: Translate('header/nav/search')
    };

    return <SearchInputBase {...props} ref={ref} labels={labels} />;
});

export default TranslatedSearchInputBase;
